<template>
  <div class="openapi-page" v-loading.fullscreen.lock="state.fullLoading">
    <el-card>
      <template #header>
        <div class="page-header">
          <span>接口配置</span>
          <el-button type="primary" @click="openDialog()">新增接口</el-button>
        </div>
      </template>

      <el-form :model="search" label-width="80px" class="search-form">
        <el-row :gutter="16">
          <el-col :span="6">
            <el-form-item label="接口编码">
              <el-input v-model="search.interfaceCode" clearable @keyup.enter="searchData" />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="接口名称">
              <el-input v-model="search.interfaceName" clearable @keyup.enter="searchData" />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="产品类型">
              <el-input v-model="search.productType" clearable @keyup.enter="searchData" />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="状态">
              <el-select v-model="search.status" clearable style="width: 100%">
                <el-option label="启用" value="ENABLED" />
                <el-option label="停用" value="DISABLED" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <div class="search-actions">
          <el-button type="primary" @click="searchData">查询</el-button>
          <el-button @click="resetSearch">重置</el-button>
        </div>
      </el-form>

      <el-table :data="tableData" border height="calc(100vh - 310px)" v-loading="state.tableLoading">
        <el-table-column prop="interfaceCode" label="接口编码" min-width="180" />
        <el-table-column prop="interfaceName" label="接口名称" min-width="160" />
        <el-table-column prop="handlerName" label="处理器" min-width="220" show-overflow-tooltip />
        <el-table-column prop="productType" label="产品类型" min-width="130" />
        <el-table-column prop="status" label="状态" width="90">
          <template #default="scope">
            <el-tag :type="scope.row.status === 'ENABLED' ? 'success' : 'info'">
              {{ scope.row.status === 'ENABLED' ? '启用' : '停用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" min-width="200" show-overflow-tooltip />
        <el-table-column prop="updateTime" label="更新时间" min-width="170" />
        <el-table-column fixed="right" label="操作" width="90">
          <template #default="scope">
            <el-button size="small" @click="openDialog(scope.row)">编辑</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-box">
        <el-pagination
          v-model:current-page="pagination.current"
          v-model:page-size="pagination.size"
          :page-sizes="pagination.sizes"
          :total="pagination.total"
          background
          layout="total, sizes, prev, pager, next"
          @current-change="loadData"
          @size-change="handleSizeChange"
        />
      </div>
    </el-card>

    <el-dialog v-model="dialog.visible" :title="dialog.form.id ? '编辑接口' : '新增接口'" width="580px" @closed="resetForm">
      <el-form ref="formRef" :model="dialog.form" :rules="rules" label-width="100px">
        <el-form-item label="接口编码" prop="interfaceCode">
          <el-input v-model="dialog.form.interfaceCode" placeholder="例如 ACCIDENT_CHENGHAO" />
        </el-form-item>
        <el-form-item label="接口名称" prop="interfaceName">
          <el-input v-model="dialog.form.interfaceName" />
        </el-form-item>
        <el-form-item label="处理器" prop="handlerName">
          <el-input v-model="dialog.form.handlerName" placeholder="后端接口处理器名称" />
        </el-form-item>
        <el-form-item label="产品类型" prop="productType">
          <el-input v-model="dialog.form.productType" placeholder="例如 ACCIDENT" />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="dialog.form.status">
            <el-radio label="ENABLED">启用</el-radio>
            <el-radio label="DISABLED">停用</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="dialog.form.remark" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialog.visible = false">取消</el-button>
        <el-button type="primary" :loading="state.saving" @click="saveData">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { nextTick, onMounted, reactive, ref } from "vue";
import { ElMessage } from "element-plus";
import { getOpenApiInterfacePage, saveOpenApiInterface } from "@/api/openapi";

const emptySearch = () => ({ interfaceCode: "", interfaceName: "", productType: "", status: "" });
const emptyForm = () => ({ interfaceCode: "", interfaceName: "", handlerName: "", productType: "", status: "ENABLED", remark: "" });

const search = reactive(emptySearch());
const state = reactive({ tableLoading: false, fullLoading: false, saving: false });
const tableData = ref([]);
const pagination = reactive({ current: 1, size: 20, total: 0, sizes: [20, 50, 100] });
const dialog = reactive({ visible: false, form: emptyForm() });
const formRef = ref();
const rules = {
  interfaceCode: [{ required: true, message: "请输入接口编码", trigger: "blur" }],
  interfaceName: [{ required: true, message: "请输入接口名称", trigger: "blur" }],
  handlerName: [{ required: true, message: "请输入处理器名称", trigger: "blur" }],
  productType: [{ required: true, message: "请输入产品类型", trigger: "blur" }],
  status: [{ required: true, message: "请选择状态", trigger: "change" }],
};

function loadData() {
  state.tableLoading = true;
  getOpenApiInterfacePage({ ...search, current: pagination.current, size: pagination.size })
    .then((res) => {
      tableData.value = res.data?.records || [];
      pagination.total = res.data?.total || 0;
    })
    .finally(() => { state.tableLoading = false; });
}

function searchData() {
  pagination.current = 1;
  loadData();
}

function resetSearch() {
  Object.assign(search, emptySearch());
  searchData();
}

function handleSizeChange() {
  pagination.current = 1;
  loadData();
}

function openDialog(row) {
  dialog.form = row ? { ...row } : emptyForm();
  dialog.visible = true;
  nextTick(() => formRef.value?.clearValidate());
}

function resetForm() {
  dialog.form = emptyForm();
  formRef.value?.clearValidate();
}

function saveData() {
  formRef.value.validate((valid) => {
    if (!valid) return;
    state.saving = true;
    saveOpenApiInterface(dialog.form)
      .then(() => {
        ElMessage.success("保存成功");
        dialog.visible = false;
        loadData();
      })
      .finally(() => { state.saving = false; });
  });
}

onMounted(loadData);
</script>

<style lang="scss" scoped>
.openapi-page { padding: 16px; }
.page-header { display: flex; align-items: center; justify-content: space-between; font-weight: 600; }
.search-form { margin-bottom: 12px; }
.search-actions { text-align: right; }
.pagination-box { margin-top: 14px; display: flex; justify-content: flex-end; }
</style>
